import React from 'react';
import { AppBar, Toolbar, Typography, IconButton, Box, Container } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  backgroundColor: '#fff',
  backdropFilter: 'blur(8px)',
  boxShadow: 'none',
  color: theme.palette.text.primary,
}));

const Logo = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  color: 'black',
  letterSpacing: -0.5,
  fontSize: '1.5rem',
}));

const ScreenTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  color: 'black',
  fontSize: '1.1rem',
}));

const LogoWithText = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  '& span': {
    fontWeight: 700,
    color: 'black',
    fontSize: '1.1rem',
    letterSpacing: -0.5,
  }
});

const LykeLogo = () => (
  <svg 
    width="24" 
    height="34" 
    viewBox="0 0 24 34" 
    fill="none" 
    xmlns="http://www.w3.org/2000/svg"
    style={{ marginTop: '10px', marginLeft: '10px' }}
  >
    <path 
      fillRule="evenodd" 
      clipRule="evenodd" 
      d="M9.29719 0.78023C10.5231 0.265123 11.837 0 13.1639 0C14.4908 0 15.8047 0.265122 17.0306 0.78023C18.2565 1.29534 19.3704 2.05034 20.3086 3.00214C21.2469 3.95393 21.9912 5.08387 22.499 6.32745C22.7757 7.00519 23.6928 9.38577 22.6184 12.4853C21.6181 15.3712 19.046 18.4478 13.6847 21.279V24.1983H9.51806V18.6392L10.7031 18.0647C16.3009 15.351 18.1145 12.7367 18.6877 11.0832C19.2578 9.43841 18.7758 8.25441 18.6495 7.94497C18.3511 7.21421 17.9137 6.55022 17.3624 5.99092C16.811 5.43162 16.1565 4.98796 15.4361 4.68526C14.7157 4.38257 13.9436 4.22678 13.1639 4.22678C12.3842 4.22678 11.6121 4.38257 10.8917 4.68526C10.0815 5.02569 9.66656 5.27969 9.17377 5.77958C8.62243 6.33888 8.18507 7.00287 7.88668 7.73364C7.58829 8.4644 7.43472 9.24763 7.43472 10.0386H3.26804C3.26804 8.69256 3.52939 7.3597 4.03717 6.11612C4.54496 4.87254 5.28922 3.74259 6.22749 2.7908C7.22431 1.77959 8.16111 1.2576 9.29719 0.78023Z" 
      fill="#215F4B"
    />
    <path 
      fillRule="evenodd" 
      clipRule="evenodd" 
      d="M0 29.4404C3.55559 27.012 7.75473 25.7371 12.0408 25.7846C16.3269 25.8322 20.4976 27.1999 24 29.7066L21.598 33.1603C18.7857 31.1476 15.4368 30.0493 11.9953 30.0112C8.55373 29.973 5.18201 30.9967 2.32703 32.9466L0 29.4404Z" 
      fill="#215F4B"
    />
  </svg>
);

function Navbar() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();

  const getScreenTitle = (pathname) => {
    switch (pathname) {
      case '/sale':
        return 'On Sale';
      case '/trending':
        return 'Trending';
      case '/likes':
        return 'My Likes';
      case '/recommendations':
        return 'For You';
      case '/settings':
        return 'Settings';
      default:
        return '';
    }
  };

  const isHomePage = location.pathname === '/' || location.pathname === '/home';
  const isMainNavPage = ['/', '/home', '/play', '/settings', '/likes'].includes(location.pathname);
  const screenTitle = getScreenTitle(location.pathname);

  return (
    user ? (
      <AppBar
        position="fixed"
        sx={{
          top: 0,
          left: 0,
          right: 0,
          zIndex: theme => theme.zIndex.appBar,
          backgroundColor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
          boxShadow: 'none',
        }}
      >
        <Container maxWidth="sm" disableGutters>
          <Toolbar sx={{ px: { xs: 2, sm: 4 } }}>
            {isHomePage ? (
              <LykeLogo />
            ) : isMainNavPage ? (
              <ScreenTitle sx={{ flexGrow: 1, textAlign: 'center' }}>
                {screenTitle}
              </ScreenTitle>
            ) : (
              <>
                <IconButton
                  edge="start"
                  onClick={() => navigate(-1)}
                  sx={{ mr: 2, color: 'black' }}
                >
                  <ArrowBackIosNewIcon />
                </IconButton>
                <LogoWithText sx={{ 
                  flexGrow: 1, 
                  justifyContent: 'center'
                }}>
                  <svg 
                    width="16" 
                    height="24" 
                    viewBox="0 0 24 34" 
                    fill="none" 
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path 
                      fillRule="evenodd" 
                      clipRule="evenodd" 
                      d="M9.29719 0.78023C10.5231 0.265123 11.837 0 13.1639 0C14.4908 0 15.8047 0.265122 17.0306 0.78023C18.2565 1.29534 19.3704 2.05034 20.3086 3.00214C21.2469 3.95393 21.9912 5.08387 22.499 6.32745C22.7757 7.00519 23.6928 9.38577 22.6184 12.4853C21.6181 15.3712 19.046 18.4478 13.6847 21.279V24.1983H9.51806V18.6392L10.7031 18.0647C16.3009 15.351 18.1145 12.7367 18.6877 11.0832C19.2578 9.43841 18.7758 8.25441 18.6495 7.94497C18.3511 7.21421 17.9137 6.55022 17.3624 5.99092C16.811 5.43162 16.1565 4.98796 15.4361 4.68526C14.7157 4.38257 13.9436 4.22678 13.1639 4.22678C12.3842 4.22678 11.6121 4.38257 10.8917 4.68526C10.0815 5.02569 9.66656 5.27969 9.17377 5.77958C8.62243 6.33888 8.18507 7.00287 7.88668 7.73364C7.58829 8.4644 7.43472 9.24763 7.43472 10.0386H3.26804C3.26804 8.69256 3.52939 7.3597 4.03717 6.11612C4.54496 4.87254 5.28922 3.74259 6.22749 2.7908C7.22431 1.77959 8.16111 1.2576 9.29719 0.78023Z" 
                      fill="#215F4B"
                    />
                    <path 
                      fillRule="evenodd" 
                      clipRule="evenodd" 
                      d="M0 29.4404C3.55559 27.012 7.75473 25.7371 12.0408 25.7846C16.3269 25.8322 20.4976 27.1999 24 29.7066L21.598 33.1603C18.7857 31.1476 15.4368 30.0493 11.9953 30.0112C8.55373 29.973 5.18201 30.9967 2.32703 32.9466L0 29.4404Z" 
                      fill="#215F4B"
                    />
                  </svg>
                  <span>lyke</span>
                </LogoWithText>
                <Box sx={{ width: 40 }} />
              </>
            )}
          </Toolbar>
        </Container>
      </AppBar>
    ) : null
  );
}

export default Navbar;
